import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormGroup,
  FormControlLabel,
  Avatar,
  CardHeader,
  InputAdornment,
  Paper,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Breadcrumbs,
  Link
} from "@mui/material";
import {
  AddTask as AddTaskIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CleaningServices as CleaningIcon,
  PriorityHigh as PriorityIcon,
  Notes as NotesIcon,
  Bed as BedIcon,
  FilterList as FilterIcon,
  Home,
  ArrowBack,
  Edit as EditIcon
} from "@mui/icons-material";

import {
  getChecklists,
  getHousekeepingStaff,
  updateTask,
  getTaskById,
  getAvailableRooms,
  getWardsAndFloors,
} from "../../services/api";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";

export const TaskEdit = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();

  const [checklists, setChecklists] = useState([]);
  const [staff, setStaff] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [wards, setWards] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBeds, setSelectedBeds] = useState([]);

  const [filters, setFilters] = useState({
    ward: "",
    floor: "",
  });

  const [form, setForm] = useState({
    room: "",
    assignedTo: "",
    checklists: [],
    dueAt: "",
    priority: "normal",
    notes: "",
    taskType: "cleaning",
    beds: [],
  });

  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  const steps = ['Location', 'Task Details', 'Assignment', 'Review'];

 
  console.log(selectedBeds);
  // Load rooms with filters
  const loadRooms = useCallback(async (newFilters = {}) => {
    setLoadingRooms(true);
    try {
      const params = { ...filters, ...newFilters };
      const res = await getAvailableRooms(params);
      setRooms(res.data?.rooms || []);
    } catch (err) {
      console.error('Room load error:', err);
      setRooms([]);
      enqueueSnackbar(
        err.response?.data?.message || "Failed to load rooms",
        { variant: "error" }
      );
    } finally {
      setLoadingRooms(false);
    }
  }, [filters, enqueueSnackbar]);

  // Update selected room when room is selected
  useEffect(() => {
    const room = rooms.find((r) => r._id === form.room);
    setSelectedRoom(room || null);
    if (!room || room._id !== form.room) {
      // setSelectedBeds([]);
      setForm(prev => ({ ...prev, beds: [] }));
    }
  }, [form.room, rooms]);



   // Load initial data and task details
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Load task details first
        const taskResponse = await getTaskById(id);
        const task = taskResponse.data?.task || taskResponse.data || taskResponse;
        console.log(task);
        // Load other data in parallel
        const [chk, stf, wf] = await Promise.all([
          getChecklists(),
          getHousekeepingStaff(),
          getWardsAndFloors(),
        ]);

        setChecklists(chk.data?.items || chk.data || []);
        setStaff(stf.data || []);
        setWards(wf.data?.wards || []);
        setFloors(wf.data?.floors || []);

        // Set form with existing task data
        setForm({
          room: task.room?._id || task.room || "",
          assignedTo: task.assignedTo?._id || task.assignedTo || "",
          checklists: Array.isArray(task.checklists)
            ? task.checklists.map(c => typeof c === "object" ? c._id : c)
            : task.checklist
              ? [typeof task.checklist === "object" ? task.checklist._id : task.checklist]
              : [],
          dueAt: task.dueAt ? new Date(task.dueAt).toISOString().slice(0, 16) :
            new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16),
          priority: task.priority || "normal",
          notes: task.notes || "",
          taskType: task.taskType || "cleaning",
          beds: task.beds || [],
        });

        // Set selected beds if available
        if (task.beds && Array.isArray(task.beds)) {
          setSelectedBeds(task.beds.map(bed => bed.bed_no));
        }





        // Set filters based on task room if available
        if (task.room) {
          const newFilters = {
            ward: task.room.ward?._id || task.ward?._id || "",
            floor: task.room.floor?._id || task.floor?._id || "",
          };
          setFilters(newFilters);
          await loadRooms(newFilters);
        } else {
          await loadRooms();
        }

      } catch (err) {
        console.error('Initial data load error:', err);
        enqueueSnackbar(
          err.response?.data?.message || "Failed to load task data",
          { variant: "error" }
        );
        navigate(-1); // Go back if task not found
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadInitialData();
    }
  }, [id, enqueueSnackbar, navigate]);

  // Filter handlers
  const handleWardChange = (ward) => {
    const newFilters = { ...filters, ward };
    setFilters(newFilters);
    setForm(prev => ({ ...prev, room: "", beds: [] }));
    setSelectedBeds([]);
    loadRooms(newFilters);
  };

  const handleFloorChange = (floor) => {
    const newFilters = { ...filters, floor };
    setFilters(newFilters);
    setForm(prev => ({ ...prev, room: "", beds: [] }));
    setSelectedBeds([]);
    loadRooms(newFilters);
  };

  // Handle multiple checklist selection
  const handleChecklistChange = (event) => {
    const { value } = event.target;
    setForm(prev => ({
      ...prev,
      checklists: typeof value === 'string' ? value.split(',') : value
    }));
  };

  // Handle bed selection
  const handleBedSelection = (bedNo, isSelected) => {
    let updatedBeds;
    if (isSelected) {
      updatedBeds = [...selectedBeds, bedNo];
    } else {
      updatedBeds = selectedBeds.filter(bedNumber => bedNumber !== bedNo);
    }
    setSelectedBeds(updatedBeds);
    setForm(prev => ({ ...prev, beds: updatedBeds }));
  };

  // Select all beds in room
  const handleSelectAllBeds = () => {
    if (!selectedRoom || !selectedRoom.beds) return;

    const allBedNumbers = selectedRoom.beds.map(bed => bed.bed_no);
    setSelectedBeds(allBedNumbers);
    setForm(prev => ({ ...prev, beds: allBedNumbers }));
  };

  // Clear all bed selections
  const handleClearAllBeds = () => {
    setSelectedBeds([]);
    setForm(prev => ({ ...prev, beds: [] }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!form.room) newErrors.room = "Room is required";
    if (!form.assignedTo) newErrors.assignedTo = "Staff assignment is required";
    if (!form.checklists || form.checklists.length === 0) newErrors.checklists = "At least one checklist is required";
    if (!form.taskType) newErrors.taskType = "Task type is required";

    if (!form.dueAt) {
      newErrors.dueAt = "Due date is required";
    } else if (new Date(form.dueAt) <= new Date()) {
      newErrors.dueAt = "Due date must be in the future";
    }

    if (selectedRoom && selectedRoom.beds && selectedRoom.beds.length > 0 && form.beds.length === 0) {
      newErrors.beds = "Please select at least one bed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleUpdate = async () => {
    if (!validateForm()) {
      enqueueSnackbar("Please fix all errors before submitting", { variant: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        dueAt: new Date(form.dueAt).toISOString(),
        checklists: form.checklists,
        beds: form.beds,
        checklist: form.checklists.length > 0 ? form.checklists[0] : null
      };

      console.log('Updating task with payload:', payload);

      await updateTask(id, payload);
      enqueueSnackbar("Task updated successfully!", { variant: "success" });

      setTimeout(() => {
        navigate("/hospital/housekeeping-and-linen-management/housekeeping/task-list");
      }, 1500);
    } catch (err) {
      console.error('Update task error:', err);
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(', ') ||
        "Failed to update task";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form to original values
  const handleReset = async () => {
    try {
      setLoading(true);
      const taskResponse = await getTaskById(id);
      const task = taskResponse.data?.task || taskResponse.data || taskResponse;

      setForm({
        room: task.room?._id || task.room || "",
        assignedTo: task.assignedTo?._id || task.assignedTo || "",
        checklists: Array.isArray(task.checklists)
          ? task.checklists.map(c => typeof c === "object" ? c._id : c)
          : task.checklist
            ? [typeof task.checklist === "object" ? task.checklist._id : task.checklist]
            : [],

        dueAt: task.dueAt ? new Date(task.dueAt).toISOString().slice(0, 16) :
          new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16),
        priority: task.priority || "normal",
        notes: task.notes || "",
        taskType: task.taskType || "cleaning",
        beds: task.beds || [],
      });

      setSelectedBeds(task.beds || []);

      if (task.room) {
        const newFilters = {
          ward: task.room.ward?._id || task.ward?._id || "",
          floor: task.room.floor?._id || task.floor?._id || "",
        };
        setFilters(newFilters);
        await loadRooms(newFilters);
      }

      setErrors({});
      enqueueSnackbar("Form has been reset to original values", { variant: "info" });
    } catch (err) {
      enqueueSnackbar("Failed to reset form", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Step navigation
  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  if (loading) {
    return (
      <Box sx={{
        p: 8,
        textAlign: "center",
        backgroundColor: 'grey.50',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600, mb: 1 }}>
          Loading Task Edit Form
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Loading task details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/hospital')}
          sx={{ cursor: 'pointer', fontSize: '1.1rem' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Hospital
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/hospital/housekeeping-and-linen-management/housekeeping/task-list')}
          sx={{ cursor: 'pointer', fontSize: '1.1rem' }}
        >
          Housekeeping
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/hospital/housekeeping-and-linen-management/housekeeping/task-list')}
          sx={{ cursor: 'pointer', fontSize: '1.1rem' }}
        >
          Task List
        </Link>
        <Typography color="text.primary" sx={{ fontSize: '1.1rem' }}>
          Edit Task
        </Typography>
      </Breadcrumbs>

      {/* Header Section */}
      <Card sx={{
        mb: 4,
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <EditIcon sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography
                variant="h2"
                fontWeight={800}
                sx={{
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Edit Task
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Modify housekeeping task details and assignments
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Progress Stepper */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '1.1rem',
                      fontWeight: 600
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Card sx={{
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        maxWidth: 1200,
        mx: 'auto'
      }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.8rem' }}>
              Edit Task Details
            </Typography>
          }
          subheader={
            <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
              Update the task information as required
            </Typography>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />

        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Room & Location Section */}
            <Grid item xs={12}>
              <SectionHeader
                icon={<LocationIcon sx={{ fontSize: 32 }} />}
                title="Room & Location"
                subtitle="Update the location where the task needs to be performed"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SelectField
                label="Filter by Ward"
                value={filters.ward}
                onChange={(e) => handleWardChange(e.target.value)}
                items={wards}
                getLabel={(w) => `${w.name} ${w.price_category ? `(${w.price_category})` : ""}`}
                loading={loading}
                icon={<FilterIcon />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SelectField
                label="Filter by Floor"
                value={filters.floor}
                onChange={(e) => handleFloorChange(e.target.value)}
                items={floors}
                getLabel={(f) => f.name}
                loading={loading}
                icon={<FilterIcon />}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.room}>
                <InputLabel sx={{ fontSize: '1.1rem' }}>Select Room</InputLabel>
                <Select
                  value={form.room}
                  label="Select Room"
                  onChange={(e) => handleFieldChange('room', e.target.value)}
                  disabled={loadingRooms}
                  sx={{ fontSize: '1.1rem' }}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    </InputAdornment>
                  }
                >
                  {loadingRooms ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 2 }} />
                      <Typography sx={{ fontSize: '1.1rem' }}>
                        Loading available rooms...
                      </Typography>
                    </MenuItem>
                  ) : rooms.length === 0 ? (
                    <MenuItem disabled>
                      <Typography sx={{ fontSize: '1.1rem' }}>
                        No rooms available for selected filters
                      </Typography>
                    </MenuItem>
                  ) : (
                    rooms.map((r) => (
                      <MenuItem key={r._id} value={r._id} sx={{ fontSize: '1.1rem' }}>
                        <RoomOption r={r} />
                      </MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText sx={{ fontSize: '1rem' }}>{errors.room}</FormHelperText>
              </FormControl>
            </Grid>

            {selectedRoom && (
              <Grid item xs={12}>
                <SelectedRoomCard
                  room={selectedRoom}
                  selectedBeds={selectedBeds}
                  onBedSelection={handleBedSelection}
                  onSelectAllBeds={handleSelectAllBeds}
                  onClearAllBeds={handleClearAllBeds}
                  error={errors.beds}
                />
              </Grid>
            )}

            {/* Task Details Section */}
            <Grid item xs={12}>
              <SectionHeader
                icon={<AssignmentIcon sx={{ fontSize: 32 }} />}
                title="Task Details"
                subtitle="Update the task requirements and specifications"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SelectField
                required
                error={errors.assignedTo}
                label="Assign To Staff"
                value={form.assignedTo}
                onChange={(e) => handleFieldChange('assignedTo', e.target.value)}
                items={staff}
                getLabel={(s) => `${s.first_name} ${s.last_name} - ${s.designation || 'Staff'}`}
                loading={loading}
                icon={<PersonIcon />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SelectField
                label="Task Type"
                value={form.taskType}
                onChange={(e) => handleFieldChange('taskType', e.target.value)}
                items={[
                  { _id: "cleaning", name: "🧹 Regular Cleaning" },
                  { _id: "deep_cleaning", name: "✨ Deep Cleaning" },
                  { _id: "disinfection", name: "🧴 Disinfection" },
                  { _id: "linen_change", name: "🛏️ Linen Change" },
                  { _id: "maintenance", name: "🔧 Maintenance" },
                  { _id: "inspection", name: "🔍 Inspection" },
                ]}
                getLabel={(i) => i.name}
                icon={<CleaningIcon />}
              />
            </Grid>

            {/* Multiple Checklists Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.checklists}>
                <InputLabel sx={{ fontSize: '1.1rem' }}>Select Checklists</InputLabel>
                <Select
                  multiple
                  value={form.checklists}
                  onChange={handleChecklistChange}
                  input={<OutlinedInput label="Select Checklists" sx={{ fontSize: '1.1rem' }} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selected.map((value) => {
                        const checklist = checklists.find(c => c._id === value);
                        return (
                          <Chip
                            key={value}
                            label={checklist?.title || value}
                            size="medium"
                            sx={{ fontSize: '1rem', fontWeight: 600 }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{ fontSize: '1.1rem' }}
                  startAdornment={
                    <InputAdornment position="start">
                      <AssignmentIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    </InputAdornment>
                  }
                >
                  {checklists.map((checklist) => (
                    <MenuItem key={checklist._id} value={checklist._id} sx={{ fontSize: '1.1rem' }}>
                      <Checkbox checked={form.checklists.indexOf(checklist._id) > -1} />
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {checklist.title}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                            {checklist.items?.length || 0} items
                          </Typography>
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ fontSize: '1rem' }}>
                  {errors.checklists || "Select one or more checklists for this task"}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <SelectField
                label="Task Priority"
                value={form.priority}
                onChange={(e) => handleFieldChange('priority', e.target.value)}
                items={[
                  { _id: "low", name: "🟢 Low Priority" },
                  { _id: "normal", name: "🔵 Normal Priority" },
                  { _id: "high", name: "🟡 High Priority" },
                  { _id: "urgent", name: "🔴 Urgent Priority" },
                ]}
                getLabel={(i) => i.name}
                icon={<PriorityIcon />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="datetime-local"
                fullWidth
                label="Due Date & Time"
                value={form.dueAt}
                onChange={(e) => handleFieldChange('dueAt', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                  sx: { fontSize: '1.1rem' }
                }}
                InputProps={{
                  sx: { fontSize: '1.1rem', padding: '12px 14px' },
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.dueAt}
                helperText={
                  <Typography sx={{ fontSize: '1rem' }}>
                    {errors.dueAt || "When should the task be completed?"}
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Notes & Instructions"
                value={form.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                placeholder="Provide specific instructions, special requirements, or additional details for the housekeeping staff..."
                InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                InputProps={{
                  sx: { fontSize: '1.1rem' },
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                      <NotesIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    </InputAdornment>
                  ),
                }}
                helperText={
                  <Typography sx={{ fontSize: '1rem' }}>
                    Optional: Add any special instructions or requirements for this task
                  </Typography>
                }
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pt={4}
                sx={{ borderTop: '2px solid', borderColor: 'divider' }}
              >
                <Box display="flex" gap={2}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                    sx={{
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="secondary"
                    onClick={handleReset}
                    disabled={submitting}
                    sx={{
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5
                    }}
                  >
                    Reset Changes
                  </Button>
                </Box>

                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    disabled={submitting}
                    onClick={handleUpdate}
                    startIcon={
                      submitting ?
                        <CircularProgress size={22} color="inherit" /> :
                        <EditIcon sx={{ fontSize: 28 }} />
                    }
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      px: 5,
                      py: 1.5,
                      minWidth: 180,
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(245, 87, 108, 0.3)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {submitting ? "Updating Task..." : "Update Task"}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

// Enhanced Helper Components with larger fonts
const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
      {icon}
      <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem', color: 'primary.main' }}>
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary', ml: 6 }}>
        {subtitle}
      </Typography>
    )}
    <Divider sx={{ mt: 2 }} />
  </Box>
);

const RoomOption = ({ r }) => (
  <Box>
    <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
      🏨 Room {r.room_no} — {r.location}
    </Typography>
    <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary', mt: 0.5 }}>
      {r.ward?.name} • {r.floor?.name} • {r.occupied_beds}/{r.bed_count} beds occupied
      {r.occupancy_status && ` • ${r.occupancy_status}`}
    </Typography>
  </Box>
);

const SelectedRoomCard = ({
  room,
  selectedBeds,
  onBedSelection,
  onSelectAllBeds,
  onClearAllBeds,
  error
}) => {
  const hasBeds = room.beds && room.beds.length > 0;
  const allBedsSelected = hasBeds && selectedBeds.length === room.beds.length;

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        border: '2px solid',
        borderColor: 'primary.light',
        backgroundColor: 'primary.light',
        background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
        borderRadius: 3
      }}
    >
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: 'primary.main' }}>
              <BedIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.4rem' }}>
                Selected Room Details
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                Room {room.room_no} - {room.location}
              </Typography>
            </Box>
          </Box>
          {hasBeds && (
            <Box>
              <Button
                size="large"
                onClick={onSelectAllBeds}
                sx={{ mr: 1, fontSize: '1rem', fontWeight: 600 }}
              >
                Select All
              </Button>
              <Button
                size="large"
                onClick={onClearAllBeds}
                sx={{ fontSize: '1rem', fontWeight: 600 }}
              >
                Clear All
              </Button>
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Chip
            label={`Room ${room.room_no}`}
            variant="filled"
            color="primary"
            sx={{ fontSize: '1rem', fontWeight: 600, py: 1 }}
          />
          <Chip
            label={`Ward: ${room.ward?.name}`}
            variant="outlined"
            sx={{ fontSize: '1rem', fontWeight: 600, py: 1 }}
          />
          <Chip
            label={`Floor: ${room.floor?.name}`}
            variant="outlined"
            sx={{ fontSize: '1rem', fontWeight: 600, py: 1 }}
          />
          <Chip
            label={`Beds: ${room.occupied_beds}/${room.bed_count} occupied`}
            color={room.occupied_beds > 0 ? "warning" : "success"}
            sx={{ fontSize: '1rem', fontWeight: 600, py: 1 }}
          />
        </Stack>

        {/* Bed Selection */}
        {hasBeds && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 2 }}>
              🛏️ Select Beds ({selectedBeds.length} of {room.beds.length} selected)
            </Typography>
            <FormGroup sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
              {room.beds.map((bed, index) => {
                const bedNo = bed.bed_no;
                const isOccupied = bed.occupy_status !== 'F';
                const isSelected = selectedBeds.includes(bedNo);
                const displayStatus = getBedDisplayStatus(bed.occupy_status);

                return (
                  <Paper
                    key={bedNo}
                    elevation={isSelected ? 4 : 1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `2px solid ${isSelected ? 'primary.main' : 'transparent'}`,
                      backgroundColor: isSelected ? 'primary.light' : 'background.paper',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      }
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => onBedSelection(bedNo, e.target.checked)}
                          color={isOccupied ? "warning" : "primary"}
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 28 }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            Bed {bedNo}
                          </Typography>
                          <Chip
                            label={displayStatus}
                            size="small"
                            color={isOccupied ? "warning" : "success"}
                            variant="filled"
                            sx={{ fontSize: '0.9rem', fontWeight: 600 }}
                          />
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </Paper>
                );
              })}
            </FormGroup>
            {error && (
              <Alert severity="error" sx={{ mt: 2, fontSize: '1.1rem' }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {!hasBeds && (
          <Alert severity="info" sx={{ fontSize: '1.1rem' }}>
            No beds available in this room
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

const SelectField = ({
  label,
  value,
  onChange,
  items,
  getLabel,
  required,
  error,
  loading = false,
  disabled = false,
  icon
}) => (
  <FormControl
    fullWidth
    required={required}
    error={!!error}
    disabled={disabled || loading}
  >
    <InputLabel sx={{ fontSize: '1.1rem' }}>{label}</InputLabel>
    <Select
      value={value}
      label={label}
      onChange={onChange}
      sx={{ fontSize: '1.1rem' }}
      startAdornment={
        icon && (
          <InputAdornment position="start">
            {React.cloneElement(icon, { sx: { color: 'primary.main', fontSize: 28 } })}
          </InputAdornment>
        )
      }
    >
      <MenuItem value="" sx={{ fontSize: '1.1rem' }}>
        <em>Select {label}</em>
      </MenuItem>
      {loading ? (
        <MenuItem disabled>
          <CircularProgress size={20} sx={{ mr: 2 }} />
          <Typography sx={{ fontSize: '1.1rem' }}>Loading...</Typography>
        </MenuItem>
      ) : items.length === 0 ? (
        <MenuItem disabled sx={{ fontSize: '1.1rem' }}>
          No options available
        </MenuItem>
      ) : (
        items.map((it) => (
          <MenuItem key={it._id} value={it._id} sx={{ fontSize: '1.1rem' }}>
            {getLabel(it)}
          </MenuItem>
        ))
      )}
    </Select>
    {error && <FormHelperText sx={{ fontSize: '1rem' }}>{error}</FormHelperText>}
  </FormControl>
);

// Helper function to display bed status
const getBedDisplayStatus = (status) => {
  switch (status) {
    case 'F': return '🟢 Free';
    case 'C': return '🟡 Occupied';
    case 'B': return '🔴 Blocked';
    case 'R': return '🟠 Reserved';
    default: return '⚪ Unknown';
  }
};